import React, { useState, useEffect, useCallback } from 'react';
import { Download, Calendar, Clock, User, MapPin, Globe, RefreshCw, FileText } from 'react-feather';
import './FormSubmissions.css';
import logger from '../../../../../../utils/logger';

function FormSubmissions({ selectedChatbotId, forms }) {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedForm, setSelectedForm] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

    const loadSubmissions = useCallback(async () => {
        if (!selectedChatbotId) return;

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`/api/form-submissions?chatbotId=${selectedChatbotId}&formId=${selectedForm}`);
            if (response.ok) {
                const data = await response.json();
                setSubmissions(data.submissions || []);
            } else {
                throw new Error('Failed to load submissions');
            }
        } catch (err) {
            logger.error('Error loading submissions:', err);
            setError('Failed to load form submissions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedChatbotId, selectedForm]);

    // Load submissions when chatbot or form selection changes
    useEffect(() => {
        if (selectedChatbotId) {
            loadSubmissions();
        }
    }, [selectedChatbotId, selectedForm, loadSubmissions]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedSubmissions = [...submissions].sort((a, b) => {
        if (sortConfig.direction === 'asc') {
            return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
        }
        return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });

    const exportToCSV = () => {
        if (submissions.length === 0) return;

        // Get all unique field names
        const allFieldNames = [...new Set(submissions.flatMap(sub => Object.keys(sub.formData || {})))];

        // Create CSV headers
        const headers = ['Date', 'Time', 'Form Step', 'User ID', 'Country', 'IP Address', ...allFieldNames];

        // Create CSV rows
        const csvRows = [
            headers.join(','),
            ...submissions.map(sub => {
                const row = [
                    new Date(sub.timestamp).toLocaleDateString(),
                    new Date(sub.timestamp).toLocaleTimeString(),
                    sub.formStep || 'N/A',
                    sub.userId || 'Anonymous',
                    sub.country || 'Unknown',
                    sub.ipAddress || 'Unknown',
                    ...allFieldNames.map(field => sub.formData?.[field] || '')
                ];
                return row.map(cell => `"${cell}"`).join(',');
            })
        ];

        // Download CSV
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `form-submissions-${selectedChatbotId}-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get unique field names for table columns
    const allFieldNames = [...new Set(submissions.flatMap(sub => Object.keys(sub.formData || {})))];

    return (
        <div className="form-submissions-container">
            <div className="submissions-header">
                <div className="submissions-title">
                    <h2>Form Submissions</h2>
                    <p>View and export user form submissions</p>
                </div>

                <div className="submissions-actions">
                    <button
                        className="refresh-btn"
                        onClick={loadSubmissions}
                        disabled={loading}
                    >
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                        Refresh
                    </button>

                    {submissions.length > 0 && (
                        <button
                            className="export-btn"
                            onClick={exportToCSV}
                        >
                            <Download size={16} />
                            Export CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Form Filter */}
            {forms.length > 0 && (
                <div className="form-filter">
                    <label>Show submissions for:</label>
                    <select
                        value={selectedForm}
                        onChange={(e) => setSelectedForm(e.target.value)}
                        className="form-select"
                    >
                        <option value="all">All Forms</option>
                        {forms.map((form) => (
                            <option key={form.id} value={form.id}>
                                Step {form.step}: {form.prompt.substring(0, 50)}...
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Content */}
            {loading && (
                <div className="loading-state">
                    <RefreshCw size={24} className="spinning" />
                    <p>Loading submissions...</p>
                </div>
            )}

            {error && (
                <div className="error-state">
                    <p>{error}</p>
                    <button onClick={loadSubmissions}>Try Again</button>
                </div>
            )}

            {!loading && !error && submissions.length === 0 && (
                <div className="empty-state">
                    <FileText size={48} />
                    <h3>No form submissions yet</h3>
                    <p>User form submissions will appear here once they start filling out your forms.</p>
                </div>
            )}

            {!loading && !error && submissions.length > 0 && (
                <div className="submissions-table-container">
                    <div className="submissions-count">
                        <span>{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="table-scroll">
                        <table className="submissions-table">
                            <thead>
                                <tr>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort('timestamp')}
                                    >
                                        <Calendar size={14} />
                                        Date
                                        {sortConfig.key === 'timestamp' && (
                                            <span className="sort-indicator">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>
                                    <th>
                                        <Clock size={14} />
                                        Time
                                    </th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort('formStep')}
                                    >
                                        Form Step
                                        {sortConfig.key === 'formStep' && (
                                            <span className="sort-indicator">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>
                                    <th>
                                        <User size={14} />
                                        User
                                    </th>
                                    <th>
                                        <MapPin size={14} />
                                        Country
                                    </th>
                                    <th>
                                        <Globe size={14} />
                                        IP Address
                                    </th>
                                    {allFieldNames.map(fieldName => (
                                        <th key={fieldName} className="field-column">
                                            {fieldName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSubmissions.map((submission, index) => (
                                    <tr key={index}>
                                        <td>{formatDate(submission.timestamp)}</td>
                                        <td>{formatTime(submission.timestamp)}</td>
                                        <td>
                                            <span className="form-step-badge">
                                                Step {submission.formStep || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="user-id">
                                                {submission.userId || 'Anonymous'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="country">
                                                {submission.country || 'Unknown'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="ip-address">
                                                {submission.ipAddress || 'Unknown'}
                                            </span>
                                        </td>
                                        {allFieldNames.map(fieldName => (
                                            <td key={fieldName} className="field-data">
                                                {submission.formData?.[fieldName] || '—'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FormSubmissions; 
